import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { Building2, User, Mail, Globe, Users, Phone, MapPin, DollarSign, TrendingUp, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BusinessSettingsConfiguration = () => {
  const { profile: userProfile, updateProfile } = useAuth();
  
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    surname: '',
    email: '',
    phoneNumber: '',
    country: '',
    businessName: '',
    domain: '',
    employeeCount: '',
    currency: 'ZAR',
    monthlyRevenue: '',
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load user profile on mount
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      const profile = userProfile;
      
      // Split full_name into name and surname if possible
      const nameParts = profile?.fullName?.split(' ') || ['', ''];
      const firstName = nameParts?.[0] || '';
      const lastName = nameParts?.slice(1)?.join(' ') || '';
      
      setCompanyInfo({
        name: firstName,
        surname: lastName,
        email: profile?.email || '',
        phoneNumber: profile?.phoneNumber || '',
        country: profile?.country || '',
        businessName: profile?.businessName || '',
        domain: profile?.domain || '',
        employeeCount: profile?.employeeCount || '',
        currency: profile?.currency || 'ZAR',
        monthlyRevenue: profile?.monthlyRevenue || '',
      });
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err?.message || 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanyInfo((prev) => ({ ...prev, [field]: value }));
    // Clear messages when user starts typing
    if (successMessage) setSuccessMessage('');
    if (error) setError('');
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Combine name and surname back to full_name
      const fullName = `${companyInfo?.name} ${companyInfo?.surname}`?.trim();

      // Prepare update data
      const updateData = {
        fullName,
        phoneNumber: companyInfo?.phoneNumber,
        country: companyInfo?.country,
        businessName: companyInfo?.businessName,
        domain: companyInfo?.domain,
        employeeCount: companyInfo?.employeeCount ? parseInt(companyInfo?.employeeCount) : null,
        currency: companyInfo?.currency,
        monthlyRevenue: companyInfo?.monthlyRevenue ? parseFloat(companyInfo?.monthlyRevenue) : null,
      };

      // CRITICAL FIX: Use AuthContext's updateProfile method
      // This updates both database AND global state immediately
      await updateProfile(updateData);
      
      // Show success message
      setSuccessMessage('Account information saved successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <Header />
        <main className="ml-64 mt-16 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Account Info Section */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-blue-50">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Account Info</h3>
                <p className="text-sm text-slate-600">
                  Basic information about your business
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={companyInfo?.name}
                    onChange={(e) => handleCompanyChange('name', e?.target?.value)}
                    placeholder="John"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Surname <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={companyInfo?.surname}
                    onChange={(e) => handleCompanyChange('surname', e?.target?.value)}
                    placeholder="Doe"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={companyInfo?.email}
                    onChange={(e) => handleCompanyChange('email', e?.target?.value)}
                    placeholder="john.doe@example.com"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-slate-50"
                    disabled
                  />
                  <p className="text-xs text-slate-500 mt-2">Email cannot be changed</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="tel"
                    value={companyInfo?.phoneNumber}
                    onChange={(e) => handleCompanyChange('phoneNumber', e?.target?.value)}
                    placeholder="+27 12 345 6789"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={companyInfo?.country}
                    onChange={(e) => handleCompanyChange('country', e?.target?.value)}
                    placeholder="South Africa"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={companyInfo?.businessName}
                    onChange={(e) => handleCompanyChange('businessName', e?.target?.value)}
                    placeholder="Acme Corporation"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Domain <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={companyInfo?.domain}
                    onChange={(e) => handleCompanyChange('domain', e?.target?.value)}
                    placeholder="example.com"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Employee Count <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="number"
                    value={companyInfo?.employeeCount}
                    onChange={(e) => handleCompanyChange('employeeCount', e?.target?.value)}
                    placeholder="10"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                    min="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Currency <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    value={companyInfo?.currency}
                    onChange={(e) => handleCompanyChange('currency', e?.target?.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white"
                    required
                  >
                    <option value="ZAR">South African Rand (ZAR)</option>
                    <option value="USD">US Dollar (USD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="GBP">British Pound (GBP)</option>
                    <option value="AUD">Australian Dollar (AUD)</option>
                    <option value="CAD">Canadian Dollar (CAD)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Monthly Revenue <span className="text-slate-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <TrendingUp className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="number"
                    value={companyInfo?.monthlyRevenue}
                    onChange={(e) => handleCompanyChange('monthlyRevenue', e?.target?.value)}
                    placeholder="50000"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    min="0"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Average monthly revenue in {companyInfo?.currency === 'ZAR' ? 'ZAR' : 'USD'}
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className={`px-8 py-3 rounded-xl font-semibold shadow-lg transition-all ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed' :'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
              }`}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </span>
              ) : (
                'Save Account Info'
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BusinessSettingsConfiguration;